import { NetworkStatus } from '@apollo/client';
import { faCommentPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { chatListUnreadSubscribeToMore } from '../../apollo/merge';
import {
  App,
  ChatRoomKind,
  ChatRoomsOrder,
  OrderDirection,
  useChatsQuery,
} from '../../graphql/types';
import { notEmpty } from '../../utils/notEmpty';
import {
  EventCategories,
  EventLabels,
  EventSubCategories,
} from '../../utils/trackingEvents';
import { useChatContext } from '../Chat/Context';
import { ButtonSize } from '../reusable/Button';
import { IconButton } from '../reusable/Button/IconButton';
import Text, { TextKind } from '../reusable/Text';
import { ChatList } from './ChatList';

interface MultiChatListProps {
  onClick: () => void;
}

export const MultiChatList: React.FC<MultiChatListProps> = ({ onClick }) => {
  const { openChat, chatListOpen, filterByUnread } = useChatContext();
  const [subscribedChats, setSubscribedChats] = useState<
    Record<string, () => void>
  >({});

  const [nameSearch, setNameSearch] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<OrderDirection>(
    OrderDirection.Desc,
  );

  const { data, loading, error, fetchMore, subscribeToMore, networkStatus } =
    useChatsQuery({
      variables: {
        input: {
          first: 15,
        },
        orderBy: {
          order: ChatRoomsOrder.LastMessageReceivedAt,
          direction: sortOrder,
        },
        filters: {
          userName: nameSearch,
          apps: [App.Heart],
          kinds: [ChatRoomKind.Direct],
          isArchived: false,
          hasUnreadMessages: filterByUnread ? true : undefined,
          memberHasSignedIn: true,
        },
      },
      notifyOnNetworkStatusChange: true,
      onCompleted: (completedData) => {
        const loadedChatIds =
          completedData.self?.chatRooms.edges
            .map(({ node }) => node.id)
            .filter(
              (chatId) => !Object.keys(subscribedChats).includes(chatId),
            ) || [];
        const newSubscriptions: Record<string, () => void> = {};
        loadedChatIds.forEach((chatId) => {
          newSubscriptions[chatId] = chatListUnreadSubscribeToMore(
            subscribeToMore,
            chatId,
          );
        });
        setSubscribedChats((prev) => ({ ...prev, ...newSubscriptions }));
      },
    });
  const chats = data?.self?.chatRooms.edges.map(({ node }) => node);

  useEffect(() => {
    return () => {
      const unsubscribes = Object.values(subscribedChats);
      if (!chatListOpen && unsubscribes.length > 0) {
        Object.values(subscribedChats).forEach((unsubscribe) => unsubscribe());
        setSubscribedChats({});
      }
    };
  }, [chatListOpen, subscribedChats]);

  const selfId = data?.self?.id;
  const hasNextPage = Boolean(data?.self?.chatRooms?.pageInfo?.hasNextPage);

  const loadMore = async () => {
    await fetchMore({
      variables: {
        input: {
          first: 15,
          after: data?.self?.chatRooms?.pageInfo?.endCursor,
        },
      },
    });
  };

  const chatsAndMembers =
    chats
      ?.map((chat) => {
        const member = chat.users.edges.find(
          ({ node }) => node.id !== selfId,
        )?.node;

        if (!member || !chat) {
          return null;
        }
        return {
          chat,
          member,
        };
      })
      .filter(notEmpty) || [];

  const handleSortClick = () => {
    setSortOrder((prev) =>
      prev === OrderDirection.Desc ? OrderDirection.Asc : OrderDirection.Desc,
    );
  };

  return (
    <ChatList
      setNameSearch={setNameSearch}
      sortOrder={sortOrder}
      onSortClick={handleSortClick}
      error={error}
      // Show spinner when initial query is loading or when fetching a query after a cache miss
      loading={
        (loading && networkStatus === NetworkStatus.loading) ||
        (networkStatus === NetworkStatus.setVariables &&
          !data?.self?.chatRooms.count)
      }
      loadMore={loadMore}
      hasNextPage={hasNextPage}
      chatsAndMembers={chatsAndMembers}
      selfId={selfId}
      onChatClick={onClick}
      Header={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text kind={TextKind.TitleM}>Chats</Text>
          <IconButton
            size={ButtonSize.Small}
            icon={<FontAwesomeIcon icon={faCommentPlus} fontSize={20} />}
            ariaLabel="create chat"
            onClick={() => openChat()}
            trackingData={{
              event_category: EventCategories.Chat,
              event_subcategory: EventSubCategories.ChatList,
              event_label: EventLabels.CreateChat,
            }}
          />
        </div>
      }
    />
  );
};
